import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  Fragment,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import ProductPageWithImg from "../productPage/ProductPageWithImg";
import ProductLocationPopUp from "../productPage/ProductLocationPopUp";
import ComparePricePopUp from "../productPage/ComparePricePopUp";
import ProductEditImg from "../productDetailsEdit/ProductEditImg";
import ProductEdit from "../productDetailsEdit/ProductEdit";
import { globalFileServer } from "../../../../axiosInstance";
import {
  getItemsPerWord,
  getProductByGtin,
  getProductsByCategory,
  getSales,
  reloedFunc,
  changeProdRank,
  clearSearch,
  setProducts,
  setCategoryItemsSource,
  clearCategoryFilters,
} from "../../../server/Product";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import Card from "../card/Card";
import ProductsLoadingGrid from "../card/ProductCardSkeleton";
import ReactTooltip from "react-tooltip";
import { CategoryUpdate, getBySlug } from "../../../server/Category";
import MobileCategoryBreadcrumb from "../../header/MobileCategoryBreadcrumb";
import MobileCategoryPills, {
  MobileCategoryPillsSkeleton,
} from "../../header/MobileCategoryPills";
import ProductAddToShoppingList from "../productPage/ProductAddToShoppingList";
import { setToggleSidebar } from "../../../server/CartsOpen";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { FiEdit2, FiSave } from "react-icons/fi";
import { DragDropContext } from "@hello-pangea/dnd";
import SortableCategoryGroup, {
  SortableCategoryGroupList,
} from "./SortableCategoryGroup";
import {
  buildGroupPositionPayload,
  findNodeBySlug,
  getCategoryUpdateErrorMessage,
  getGroupDropError,
  isCompleteGroupPositionPayload,
  makeGroupDroppableId,
  parseGroupDroppableId,
  permuteVisibleChildren,
  replaceChildrenBySlug,
  resolveFamilyReorderFromListIndices,
  GROUP_REORDER_ERROR,
} from "./categoryGroupReorder";
import {
  findCategoryBySlug,
  getCategoryLevel,
} from "../products/useCategoryUtils";
import { useProductStatusToggle } from "../../../hooks/useProductStatusToggle";
import { withProductStatus } from "../../../utils/productStatus";
import { groupSalesProductsByDepartment } from "../../../utils/groupSalesProductsByDepartment";
import { getNextVisibleSiblingCategory } from "../../../utils/categoryVisibility";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { isGtin } from "../../../utils/barcode";
import CategoryAddProductButton from "../products/CategoryAddProductButton";

const hasProducts = (node) => {
  if (!node) return false;

  if (Array.isArray(node)) return node.some(hasProducts);

  if (node.products?.length) return true;

  return node.children?.some(hasProducts) || false;
};

const countTreeProducts = (node) => {
  if (!node) return 0;
  // Flat lists (e.g. /search) are arrays of product objects.
  if (Array.isArray(node)) {
    return node.reduce((sum, child) => sum + countTreeProducts(child), 0);
  }
  if (node.productId != null) return 1;
  let count = Array.isArray(node.products) ? node.products.length : 0;
  for (const child of node.children || []) {
    count += countTreeProducts(child);
  }
  return count;
};

/** Products that appear before `slug`'s section in document order. */
const countProductsBeforeSlug = (node, slug) => {
  let count = 0;
  let found = false;
  const walk = (n) => {
    if (!n || found) return;
    if (n.slug === slug) {
      found = true;
      return;
    }
    count += Array.isArray(n.products) ? n.products.length : 0;
    for (const child of n.children || []) walk(child);
  };
  walk(node);
  return found ? count : null;
};

const HEADER_LEVEL_CLASS = {
  1: " category-section-header--level-1",
  2: " category-section-header--level-2",
  3: " category-section-header--level-3",
  4: " category-section-header--level-4",
};

/** מחלקה / קטגוריה / תת-קטגוריה / מקבץ each get their own accent color. */
const getSectionHeaderLevelClass = (categoryLevel, isFamily, withAccent) => {
  const colorLevel = isFamily
    ? 4
    : categoryLevel <= 1
      ? 1
      : Math.min(categoryLevel, 3);
  let cls = HEADER_LEVEL_CLASS[colorLevel] || HEADER_LEVEL_CLASS[1];
  if (isFamily) {
    cls = ` category-section-header--family${cls}`;
  }
  return withAccent ? `${cls} category-section-header--accent` : cls;
};

const collectDescendantCategorySlugs = (node) => {
  if (!node) return [];

  const slugs = [node.slug];

  for (const child of node.children || []) {
    slugs.push(...collectDescendantCategorySlugs(child));
  }

  return slugs;
};

const updateProductsInTree = (node, productIds, updater) => {
  if (!node) return node;
  if (Array.isArray(node)) {
    const next = node.map((child) =>
      updateProductsInTree(child, productIds, updater),
    );
    return next.some((child, index) => child !== node[index]) ? next : node;
  }

  let changed = false;
  let products = node.products;
  if (products?.length) {
    const nextProducts = products.map((product) => {
      if (!productIds.has(String(product.productId))) return product;
      changed = true;
      return updater(product);
    });
    if (changed) products = nextProducts;
  }

  let children = node.children;
  if (children?.length) {
    const nextChildren = children.map((child) =>
      updateProductsInTree(child, productIds, updater),
    );
    if (nextChildren.some((child, index) => child !== children[index])) {
      children = nextChildren;
      changed = true;
    }
  }

  return changed ? { ...node, products, children } : node;
};

const updateProductsPriceInTree = (node, productIds, price) =>
  updateProductsInTree(node, productIds, (product) => ({
    ...product,
    pricing: { ...product.pricing, price },
  }));

const updateProductsMediaInTree = (node, productIds, media) =>
  updateProductsInTree(node, productIds, (product) => ({
    ...product,
    media,
  }));

const updateProductsAvailabilityInTree = (node, productIds, availability) =>
  updateProductsInTree(node, productIds, (product) => ({
    ...product,
    availability: {
      ...product.availability,
      ...availability,
    },
  }));

const updateCatalogQuantitiesInTree = (node, quantitiesById) =>
  updateProductsInTree(node, new Set(quantitiesById.keys()), (product) => {
    const availableQuantity = quantitiesById.get(String(product.productId));
    if (!Number.isFinite(availableQuantity)) return product;
    return {
      ...product,
      availability: {
        ...product.availability,
        availableQuantity,
        availableInventory: availableQuantity,
      },
    };
  });

const updateProductsStatusInTree = (node, productIds, status) =>
  updateProductsInTree(node, productIds, (product) =>
    withProductStatus(product, status),
  );

const mergeProductPatch = (product, patch) => {
  if (!product || !patch) return product;
  const next = { ...product };
  for (const [key, value] of Object.entries(patch)) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      product[key] &&
      typeof product[key] === "object" &&
      !Array.isArray(product[key])
    ) {
      next[key] = { ...product[key], ...value };
    } else {
      next[key] = value;
    }
  }
  return next;
};

const applyProductFieldsPatch = (
  product,
  { familyPatch, productSpecificPatch, mainProductId, status } = {},
) => {
  let next = product;
  if (familyPatch) {
    next = mergeProductPatch(next, familyPatch);
  }
  if (
    productSpecificPatch &&
    String(product.productId) === String(mainProductId)
  ) {
    next = mergeProductPatch(next, productSpecificPatch);
  }
  if (status !== undefined && status !== null && status !== "") {
    next = withProductStatus(next, status);
  }
  return next;
};

const updateProductsFieldsInTree = (node, productIds, payload) =>
  updateProductsInTree(node, productIds, (product) =>
    applyProductFieldsPatch(product, payload),
  );

const CategoryPage = ({
  setCurrentSubCategory,
  pendingScrollSlug,
  setPendingScrollSlug,
}) => {
  const navigate = useNavigate();
  const dragItem = useRef();
  const dragOverItem = useRef();
  const beforeItem = useRef();
  const { id } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const alwaysShowBrand = pathname === "/search";
  const dispatch = useDispatch();
  const pendingScrollProductIdRef = useRef(
    location.state?.scrollToProductId || null,
  );
  const currentCategory = useSelector(
    (state) => state.category.currentCategory,
  );
  const categories = useSelector((state) => state.category.categories);
  const stationId = useSelector((state) => state.user.stationId);
  const role = useSelector((state) => state.user.role);
  const refresh = useSelector((state) => state.productsInCart.refresh);
  const reloed = useSelector((state) => state.displayProduct.reloed);
  const items = useSelector((state) => state.displayProduct.itemsShow);
  const categoryItemsSource = useSelector(
    (state) => state.displayProduct.categoryItemsSource,
  );
  const searchString = useSelector(
    (state) => state.displayProduct.searchString,
  );
  const isClientView = useSelector((state) => state.category.isClientView);
  const includeInactive = useSelector(
    (state) => state.category.includeInactive,
  );
  const sortKey = useSelector(
    (state) => state.displayProduct.sortKey ?? "relevant",
  );
  const [itemsShow, setItemsShow] = useState(items);
  const [selectedProd, setSelectedProd] = useState([]);
  const [productPopUp, setProductPopUp] = useState(null);
  const [productLocationPopUp, setProductLocationPopUp] = useState(null);
  const [comparePricePopUp, setComparePricePopUp] = useState(false);
  const [editImagesPopUp, setEditImagesPopUp] = useState(false);
  const [editSettingsPopUp, setEditSettingsPopUp] = useState(false);
  const [addToShopListPopUp, setAddToShopListPopUp] = useState(null);
  const [listHeartSavedByProductId, setListHeartSavedByProductId] = useState(
    {},
  );
  const [pendingListHeartProductId, setPendingListHeartProductId] =
    useState(null);
  const [countPage, _setCountPage] = useState(30);
  const [pagePlace, setPagePlace] = useState(0);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const [renamingCardId, setRenamingCardId] = useState(null);
  const [renameInputValue, setRenameInputValue] = useState("");
  const [updatedCategories, setUpdatedCategories] = useState([]);
  const [savedGroupSlug, setSavedGroupSlug] = useState(null);
  const [groupSavePending, setGroupSavePending] = useState(false);
  const groupListsRef = useRef({});
  const groupSavePendingRef = useRef(false);
  const [parentCtegory, setParentCtegory] = useState(null);
  // Mobile pills/breadcrumb selection — click + scroll-spy (like desktop SubNavbar).
  const [mobileSelectedSlug, setMobileSelectedSlug] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const currentAbortController = useRef(null);
  const isSalesPageRef = useRef(false);
  const autoAdvanceLockRef = useRef(false);
  const skipCategorySkeletonRef = useRef(false);
  const continuousExpandRef = useRef(false);
  const expandScrollRestoreRef = useRef(null);
  const pendingScrollAttemptsRef = useRef(0);
  const pendingProductScrollAttemptsRef = useRef(0);
  const MAX_PENDING_SCROLL_ATTEMPTS = 25;
  const pendingScrollDescendantSlugsRef = useRef([]);
  const pendingScrollDescendantCategorySelectorRef = useRef("");
  const pendingScrollDescendantSlugsForSlugRef = useRef(null);
  // Blocks IntersectionObserver from selecting a mid-list child while we
  // programmatically scroll to the start of a clicked subcategory.
  const programmaticScrollLockRef = useRef(false);
  const programmaticScrollUnlockTimerRef = useRef(null);
  // rAF + delay before scrollTo — cancelled on route change / new slug / unmount.
  const pendingScrollScheduleRef = useRef({ rafId: null, timeoutId: null });
  // Keeps the clicked category highlighted until the user scrolls manually.
  const pinnedSubCategoryRef = useRef(null);
  // True if the user scrolled during the programmatic lock window — the unlock
  // timer must not re-pin / snap the highlight back to the clicked category.
  const userScrolledDuringLockRef = useRef(false);

  const clearPendingScrollSchedule = () => {
    const schedule = pendingScrollScheduleRef.current;
    if (schedule.rafId != null) {
      cancelAnimationFrame(schedule.rafId);
      schedule.rafId = null;
    }
    if (schedule.timeoutId != null) {
      clearTimeout(schedule.timeoutId);
      schedule.timeoutId = null;
    }
  };

  useEffect(() => {
    if (groupSavePendingRef.current) return;
    setItemsShow(items);
  }, [items]);

  useEffect(() => {
    if (!savedGroupSlug) return undefined;
    const timeoutId = setTimeout(() => setSavedGroupSlug(null), 2000);
    return () => clearTimeout(timeoutId);
  }, [savedGroupSlug]);

  useEffect(() => {
    isSalesPageRef.current = pathname === "/sales";
  }, [pathname]);

  const closeAddToShopListPopUp = useCallback(() => {
    setAddToShopListPopUp(false);
    setPendingListHeartProductId(null);
  }, []);

  const closeComparePricePopUp = useCallback(() => {
    setComparePricePopUp(false);
  }, []);

  const closeEditImagesPopUp = useCallback(() => {
    setEditImagesPopUp(false);
  }, []);

  const closeEditSettingsPopUp = useCallback(() => {
    setEditSettingsPopUp(false);
  }, []);

  const closeProductLocationPopUp = useCallback(() => {
    setProductLocationPopUp(false);
  }, []);

  const openAdminProductPopUp = useCallback(
    (element, setOpen) => {
      setSelectedProd(element);
      setOpen(true);
      dispatch(setToggleSidebar(false));
      if (stationId && element?.productId) {
        dispatch(getProductsByCategory(stationId, element.productId));
      }
    },
    [dispatch, stationId],
  );

  const openComparePricePopUp = useCallback(
    (element) => {
      openAdminProductPopUp(element, setComparePricePopUp);
    },
    [openAdminProductPopUp],
  );

  const openEditImagesPopUp = useCallback(
    (element) => {
      openAdminProductPopUp(element, setEditImagesPopUp);
    },
    [openAdminProductPopUp],
  );

  const openEditSettingsPopUp = useCallback(
    (element) => {
      openAdminProductPopUp(element, setEditSettingsPopUp);
    },
    [openAdminProductPopUp],
  );

  const openProductLocationPopUp = useCallback(
    (element) => {
      openAdminProductPopUp(element, setProductLocationPopUp);
    },
    [openAdminProductPopUp],
  );

  const handlePriceSaved = useCallback((productsToChange, price) => {
    const productIds = new Set(
      (productsToChange || [])
        .map((product) => String(product.productId))
        .filter((id) => id && id !== "undefined" && id !== "null"),
    );
    if (!productIds.size) return;

    setItemsShow((prev) => updateProductsPriceInTree(prev, productIds, price));
    setSelectedProd((prev) =>
      prev && productIds.has(String(prev.productId))
        ? { ...prev, pricing: { ...prev.pricing, price } }
        : prev,
    );
  }, []);

  const handleMediaSaved = useCallback((productsToChange, media) => {
    const productIds = new Set(
      (productsToChange || [])
        .map((product) => String(product.productId))
        .filter((id) => id && id !== "undefined" && id !== "null"),
    );
    if (!productIds.size) return;

    setItemsShow((prev) => updateProductsMediaInTree(prev, productIds, media));
    setSelectedProd((prev) =>
      prev && productIds.has(String(prev.productId))
        ? { ...prev, media }
        : prev,
    );
  }, []);

  const handleProductFieldsSaved = useCallback(
    (productsToChange, payload) => {
      const productIds = new Set(
        (productsToChange || [])
          .map((product) => String(product.productId))
          .filter((id) => id && id !== "undefined" && id !== "null"),
      );
      if (!productIds.size) return;

      setItemsShow((prev) =>
        updateProductsFieldsInTree(prev, productIds, payload),
      );
      if (pathname === "/search" || pathname === "/sales") {
        dispatch(
          setProducts(updateProductsFieldsInTree(items, productIds, payload)),
        );
      }
      setSelectedProd((prev) =>
        prev && productIds.has(String(prev.productId))
          ? applyProductFieldsPatch(prev, payload)
          : prev,
      );
    },
    [dispatch, items, pathname],
  );

  const handleInventoryUpdated = useCallback(
    (productId, availability) => {
      if (productId == null || !availability) return;
      const productIds = new Set([String(productId)]);

      setItemsShow((prev) =>
        updateProductsAvailabilityInTree(prev, productIds, availability),
      );
      if (pathname === "/search" || pathname === "/sales") {
        dispatch(
          setProducts(
            updateProductsAvailabilityInTree(items, productIds, availability),
          ),
        );
      }
      setSelectedProd((prev) =>
        prev && productIds.has(String(prev.productId))
          ? {
              ...prev,
              availability: {
                ...prev.availability,
                ...availability,
              },
            }
          : prev,
      );
    },
    [dispatch, items, pathname],
  );

  const handleCatalogAvailabilityUpdated = useCallback(
    (entries) => {
      const quantitiesById = new Map();
      for (const entry of entries || []) {
        if (entry?.productId == null) continue;
        const availableQuantity = Number(entry.availableQuantity);
        if (!Number.isFinite(availableQuantity)) continue;
        quantitiesById.set(String(entry.productId), availableQuantity);
      }
      if (!quantitiesById.size) return;

      setItemsShow((prev) =>
        updateCatalogQuantitiesInTree(prev, quantitiesById),
      );
      if (pathname === "/search" || pathname === "/sales") {
        dispatch(
          setProducts(updateCatalogQuantitiesInTree(items, quantitiesById)),
        );
      }
      setSelectedProd((prev) => {
        if (!prev || !quantitiesById.has(String(prev.productId))) return prev;
        const availableQuantity = quantitiesById.get(String(prev.productId));
        return {
          ...prev,
          availability: {
            ...prev.availability,
            availableQuantity,
            availableInventory: availableQuantity,
          },
        };
      });
    },
    [dispatch, items, pathname],
  );

  const applyLocalProductStatus = useCallback(
    (productId, status) => {
      const productIds = new Set([String(productId)]);
      setItemsShow((prev) =>
        updateProductsStatusInTree(prev, productIds, status),
      );
      if (pathname === "/search" || pathname === "/sales") {
        dispatch(
          setProducts(updateProductsStatusInTree(items, productIds, status)),
        );
      }
      setSelectedProd((prev) =>
        prev && productIds.has(String(prev.productId))
          ? withProductStatus(prev, status)
          : prev,
      );
    },
    [dispatch, items, pathname],
  );

  const { setProductStatus, statusPendingByProductId } = useProductStatusToggle(
    applyLocalProductStatus,
  );

  const handleAdminStatusClick = useCallback(
    (product, categorySlug, nextStatus) => {
      setProductStatus(product, categorySlug, nextStatus);
    },
    [setProductStatus],
  );

  /* Wire hover tooltips to product cards after grid updates (react-tooltip scans data-tip on bind) */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      ReactTooltip.rebuild();
    });
    return () => cancelAnimationFrame(id);
  }, [items]);

  useEffect(() => {
    setParentCtegory(currentCategory);
  }, [currentCategory]);

  useEffect(() => {
    if (!pendingScrollSlug) return;
    pendingScrollAttemptsRef.current = 0;
    pendingScrollDescendantSlugsForSlugRef.current = null;
    programmaticScrollLockRef.current = true;
    pinnedSubCategoryRef.current = pendingScrollSlug;
    userScrolledDuringLockRef.current = false;
    clearPendingScrollSchedule();
    if (programmaticScrollUnlockTimerRef.current) {
      clearTimeout(programmaticScrollUnlockTimerRef.current);
      programmaticScrollUnlockTimerRef.current = null;
    }
    // Expand nested subcategory nav early so sticky height is stable before scroll.
    // Keep the clicked parent selected — do not let scroll-spy pick a mid child.
    setCurrentSubCategory(pendingScrollSlug);
    setPagePlace((p) => p + 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingScrollSlug, reloed]);

  useEffect(() => {
    if (!pendingScrollSlug || reloed) return;

    const targetNode = findCategoryBySlug(
      itemsShow ?? items,
      pendingScrollSlug,
    );

    // A category with nothing to show never renders a section — stop right
    // away instead of futilely growing the render window 25 times.
    if (targetNode && !hasProducts(targetNode)) {
      pendingScrollAttemptsRef.current = 0;
      programmaticScrollLockRef.current = false;
      pinnedSubCategoryRef.current = null;
      setPendingScrollSlug(null);
      return;
    }

    if (targetNode) {
      if (
        pendingScrollDescendantSlugsForSlugRef.current !== pendingScrollSlug
      ) {
        pendingScrollDescendantSlugsRef.current =
          collectDescendantCategorySlugs(targetNode);
        pendingScrollDescendantSlugsForSlugRef.current = pendingScrollSlug;
        pendingScrollDescendantCategorySelectorRef.current =
          pendingScrollDescendantSlugsRef.current
            .map((s) => `[data-category-id="${CSS.escape(String(s))}"]`)
            .join(", ");
      }
    } else {
      pendingScrollDescendantSlugsRef.current = [];
      pendingScrollDescendantCategorySelectorRef.current = "";
      pendingScrollDescendantSlugsForSlugRef.current = null;
    }

    const findScrollTarget = (slug) =>
      document.querySelector(
        `[data-category-scroll-anchor="${CSS.escape(slug)}"]`,
      ) ||
      document.querySelector(`[data-category-id="${CSS.escape(slug)}"]`) ||
      document.querySelector(
        `[data-parent-category-id="${CSS.escape(slug)}"]`,
      ) ||
      (pendingScrollDescendantCategorySelectorRef.current
        ? document.querySelector(
            pendingScrollDescendantCategorySelectorRef.current,
          )
        : null);

    const el = findScrollTarget(pendingScrollSlug);
    if (el) {
      pendingScrollAttemptsRef.current = 0;
      const slug = pendingScrollSlug;
      setPendingScrollSlug(null);
      setCurrentSubCategory(slug);

      const scrollToSubcategoryStart = () => {
        const target = findScrollTarget(slug);
        if (!target) {
          programmaticScrollLockRef.current = false;
          pinnedSubCategoryRef.current = null;
          return;
        }

        const stackBottom =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--shell-fixed-stack-bottom",
            ),
          ) || 170;
        const top =
          window.scrollY + target.getBoundingClientRect().top - stackBottom;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

        if (programmaticScrollUnlockTimerRef.current) {
          clearTimeout(programmaticScrollUnlockTimerRef.current);
        }
        // Release the scroll lock after smooth scroll, but keep the clicked
        // category pinned until the user scrolls manually — unless they already
        // scrolled away during the lock window.
        programmaticScrollUnlockTimerRef.current = setTimeout(() => {
          programmaticScrollLockRef.current = false;
          programmaticScrollUnlockTimerRef.current = null;
          if (userScrolledDuringLockRef.current) {
            userScrolledDuringLockRef.current = false;
            pinnedSubCategoryRef.current = null;
            return;
          }
          pinnedSubCategoryRef.current = slug;
          setCurrentSubCategory(slug);
        }, 1000);
      };

      // Nested SubNavbar mounts in a child useEffect after this state update;
      // wait one paint + a short tick so sticky offset matches before scrolling.
      clearPendingScrollSchedule();
      pendingScrollScheduleRef.current.rafId = requestAnimationFrame(() => {
        pendingScrollScheduleRef.current.rafId = null;
        pendingScrollScheduleRef.current.timeoutId = setTimeout(() => {
          pendingScrollScheduleRef.current.timeoutId = null;
          scrollToSubcategoryStart();
        }, 50);
      });
    } else if (pendingScrollAttemptsRef.current < MAX_PENDING_SCROLL_ATTEMPTS) {
      pendingScrollAttemptsRef.current += 1;
      setPagePlace((p) => p + 2);
    } else {
      pendingScrollAttemptsRef.current = 0;
      programmaticScrollLockRef.current = false;
      pinnedSubCategoryRef.current = null;
      setPendingScrollSlug(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePlace, pendingScrollSlug, reloed]);

  useEffect(() => {
    return () => {
      clearPendingScrollSchedule();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear the click-pin on any real user scroll (scrollbar drag, keys,
    // Ctrl+F, etc.). wheel/touchmove alone miss most desktop scroll methods.
    // During the programmatic lock, scroll events also fire from smooth
    // scrollTo — so only gesture listeners record a user interrupt; the unlock
    // timer then skips re-pinning so the highlight does not snap back.
    const clearPinnedOnUserScroll = () => {
      if (programmaticScrollLockRef.current) {
        userScrolledDuringLockRef.current = true;
        return;
      }
      if (!pinnedSubCategoryRef.current) return;
      pinnedSubCategoryRef.current = null;
    };

    const clearPinnedOnScroll = () => {
      // Programmatic smooth scroll emits scroll events — do not treat those as
      // a user interrupt. Only clear the pin once the lock is released.
      if (programmaticScrollLockRef.current) return;
      if (!pinnedSubCategoryRef.current) return;
      pinnedSubCategoryRef.current = null;
    };

    const clearPinnedOnScrollKey = (event) => {
      if (
        event.key !== " " &&
        event.key !== "PageDown" &&
        event.key !== "PageUp" &&
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Home" &&
        event.key !== "End"
      ) {
        return;
      }
      clearPinnedOnUserScroll();
    };

    window.addEventListener("scroll", clearPinnedOnScroll, {
      passive: true,
    });
    window.addEventListener("wheel", clearPinnedOnUserScroll, {
      passive: true,
    });
    window.addEventListener("touchmove", clearPinnedOnUserScroll, {
      passive: true,
    });
    window.addEventListener("keydown", clearPinnedOnScrollKey);

    return () => {
      window.removeEventListener("scroll", clearPinnedOnScroll);
      window.removeEventListener("wheel", clearPinnedOnUserScroll);
      window.removeEventListener("touchmove", clearPinnedOnUserScroll);
      window.removeEventListener("keydown", clearPinnedOnScrollKey);
      if (programmaticScrollUnlockTimerRef.current) {
        clearTimeout(programmaticScrollUnlockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const scrollToProductId = pendingScrollProductIdRef.current;
    if (!scrollToProductId || reloed) return;

    const el = document.querySelector(
      `[data-product-id="${CSS.escape(String(scrollToProductId))}"]`,
    );
    if (el) {
      pendingScrollProductIdRef.current = null;
      pendingProductScrollAttemptsRef.current = 0;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (location.state?.scrollToProductId) {
        navigate(pathname, { replace: true, state: {} });
      }
      return;
    }

    if (pendingProductScrollAttemptsRef.current < MAX_PENDING_SCROLL_ATTEMPTS) {
      pendingProductScrollAttemptsRef.current += 1;
      setPagePlace((p) => p + 2);
    } else {
      pendingScrollProductIdRef.current = null;
      pendingProductScrollAttemptsRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePlace, reloed, items]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (programmaticScrollLockRef.current) return;
          if (pinnedSubCategoryRef.current) return;
          if (typeof setCurrentSubCategory !== "function") return;

          const categoryId = entry.target.dataset.categoryId;
          const parentId = entry.target.dataset.parentCategoryId;
          const familyCat = entry.target.dataset.family;

          if (!categoryId) return;

          if (categoryId === parentCtegory?.slug) return;

          if (
            familyCat === "true" ||
            parentCtegory?.children.find((cat) => cat.slug === parentId)
          ) {
            if (parentId) {
              setCurrentSubCategory(parentId);
              setMobileSelectedSlug(parentId);
            }
          } else {
            setCurrentSubCategory(categoryId);
            setMobileSelectedSlug(categoryId);
          }
        });
      },
      {
        rootMargin: "-40% 0px -40% 0px",
        threshold: 0,
      },
    );

    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCtegory]);

  const observeSubCategories = useCallback(() => {
    if (typeof setCurrentSubCategory !== "function") return;
    const anchors = document.querySelectorAll(
      "[data-category-id]:not([data-category-scroll-anchor])",
    );
    anchors.forEach((el) => observerRef.current?.observe(el));
  }, [setCurrentSubCategory]);
  const handleScroll = useCallback(() => {
    if (loadingRef.current) return;

    const scrollBottom = window.innerHeight + window.scrollY;
    const docHeight = document.documentElement.scrollHeight;
    const nearBottom = scrollBottom * 1.2 >= docHeight;
    const atBottom = scrollBottom >= docHeight - 64;

    if (nearBottom) {
      loadingRef.current = true;

      const treeRoot = hasProducts(itemsShow) ? itemsShow : items;
      const totalInTree = countTreeProducts(treeRoot);
      const hasMoreToRender = totalInTree > countPage * (pagePlace + 1);

      if (hasMoreToRender) {
        setPagePlace((prev) => prev + 1);
      } else if (
        atBottom &&
        isMobile &&
        pathname.startsWith("/category") &&
        id &&
        stationId &&
        !reloed &&
        !autoAdvanceLockRef.current
      ) {
        const next = getNextVisibleSiblingCategory(
          id,
          categories,
          role,
          includeInactive,
        );
        const currentMeta = findCategoryBySlug(categories, id);
        const parentSlug = currentMeta?.parentSlug;
        // Expand leaf → parent tree in place (no jump/remount). Skip when
        // already on the parent continuous page or when this is the last sibling.
        if (next?.slug && parentSlug && treeRoot?.slug !== parentSlug) {
          autoAdvanceLockRef.current = true;
          const marker =
            document.querySelector(`[data-category-id="${CSS.escape(id)}"]`) ||
            document.querySelector(
              `[data-category-scroll-anchor="${CSS.escape(id)}"]`,
            );
          const markerTop = marker?.getBoundingClientRect().top ?? null;

          getBySlug(
            stationId,
            parentSlug,
            role ? isClientView : true,
            role ? includeInactive : false,
          )
            .then((res) => {
              if (!res || isSalesPageRef.current) {
                autoAdvanceLockRef.current = false;
                return;
              }

              const before = countProductsBeforeSlug(res, id) ?? 0;
              const currentNode = findNodeBySlug(res, id);
              const throughCurrent = before + countTreeProducts(currentNode);
              const nextPagePlace = Math.max(
                0,
                Math.ceil((throughCurrent + countPage) / countPage) - 1,
              );

              skipCategorySkeletonRef.current = true;
              continuousExpandRef.current = true;
              if (markerTop != null) {
                expandScrollRestoreRef.current = {
                  slug: id,
                  top: markerTop,
                };
              }

              dispatch(setCategoryItemsSource(res));
              setItemsShow(res);
              setPagePlace(nextPagePlace);
              setMobileSelectedSlug(id);
              navigate(`/category/${parentSlug}`, { replace: true });
            })
            .catch((err) => {
              console.error("Error expanding to parent category:", err);
              autoAdvanceLockRef.current = false;
            });
        }
      }

      setTimeout(() => {
        loadingRef.current = false;
      }, 300);
    }

    observeSubCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagePlace,
    observeSubCategories,
    itemsShow,
    items,
    countPage,
    isMobile,
    pathname,
    id,
    stationId,
    reloed,
    categories,
    role,
    includeInactive,
    isClientView,
    dispatch,
    navigate,
  ]);

  useLayoutEffect(() => {
    const restore = expandScrollRestoreRef.current;
    if (!restore) return;

    const el =
      document.querySelector(
        `[data-category-scroll-anchor="${CSS.escape(restore.slug)}"]`,
      ) ||
      document.querySelector(
        `[data-category-id="${CSS.escape(restore.slug)}"]`,
      );

    if (el) {
      const delta = el.getBoundingClientRect().top - restore.top;
      if (Math.abs(delta) > 1) {
        window.scrollBy(0, delta);
      }
    }

    expandScrollRestoreRef.current = null;
    autoAdvanceLockRef.current = false;
    observeSubCategories();
  }, [itemsShow, id, pagePlace, observeSubCategories]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    observeSubCategories();
  }, [pagePlace, observeSubCategories]);

  useEffect(() => {
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, stationId, isClientView]);

  useEffect(() => {
    if (searchString) {
      dispatch(reloedFunc());
      // Admin search always uses the admin product payload so status and other
      // staff fields stay available in תצוגת לקוח. Customer UI is still from isClientView.
      const fetchAsClient = !role;
      if (isGtin(searchString)) {
        dispatch(getProductByGtin(searchString, stationId, fetchAsClient));
      } else {
        dispatch(
          getItemsPerWord(
            searchString,
            stationId,
            "gtin,name,variant,brand,sub_brand,user_company,media,description,kosher_supervision,weight_details,weight_jump,verify_age,allow_order_when_oos,logistics_details,view_brand,categories",
            fetchAsClient,
          ),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, role, stationId]);

  const fetchCategoryListing = useCallback(
    (abortController) => {
      if (!stationId || !id) return Promise.resolve();

      return getBySlug(
        stationId,
        id,
        role ? isClientView : true,
        role ? includeInactive : false,
        abortController ? { signal: abortController.signal } : {},
      )
        .then((res) => {
          if (isSalesPageRef.current) return;
          if (res) dispatch(setCategoryItemsSource(res));
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    },
    [stationId, id, isClientView, includeInactive, dispatch, role],
  );

  useEffect(() => {
    if (!stationId || !id) return;

    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }
    const abortController = new AbortController();
    currentAbortController.current = abortController;
    fetchCategoryListing(abortController);
  }, [
    stationId,
    id,
    isClientView,
    includeInactive,
    dispatch,
    role,
    fetchCategoryListing,
  ]);

  const handlePromotionsChanged = useCallback(() => {
    if (pathname === "/sales") {
      if (stationId) dispatch(getSales(stationId, isClientView));
      return;
    }
    if (!stationId || !id) return;
    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }
    const abortController = new AbortController();
    currentAbortController.current = abortController;
    fetchCategoryListing(abortController);
  }, [pathname, stationId, id, isClientView, dispatch, fetchCategoryListing]);

  const getItems = () => {
    if (pathname !== "/search") {
      dispatch(clearSearch());

      if (pathname === "/sales") {
        if (currentAbortController.current) {
          currentAbortController.current.abort();
        }

        dispatch(reloedFunc());
        dispatch(getSales(stationId, isClientView));
      }
    }
  };

  const previousCategoryIdRef = useRef(null);

  useEffect(() => {
    if (continuousExpandRef.current) {
      continuousExpandRef.current = false;
      pinnedSubCategoryRef.current = null;
      programmaticScrollLockRef.current = false;
      userScrolledDuringLockRef.current = false;
      clearPendingScrollSchedule();
      if (programmaticScrollUnlockTimerRef.current) {
        clearTimeout(programmaticScrollUnlockTimerRef.current);
        programmaticScrollUnlockTimerRef.current = null;
      }
      return;
    }

    if (pathname.includes("/category")) {
      setCurrentSubCategory(null);
    }
    pinnedSubCategoryRef.current = null;
    programmaticScrollLockRef.current = false;
    userScrolledDuringLockRef.current = false;
    clearPendingScrollSchedule();
    if (programmaticScrollUnlockTimerRef.current) {
      clearTimeout(programmaticScrollUnlockTimerRef.current);
      programmaticScrollUnlockTimerRef.current = null;
    }
    setMobileSelectedSlug(null);
    autoAdvanceLockRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pathname, searchString, refresh]);

  useEffect(() => {
    const showCategorySkeleton = () => {
      dispatch(clearCategoryFilters({ resetSource: true }));
      dispatch(reloedFunc());
    };

    if (previousCategoryIdRef.current === null) {
      previousCategoryIdRef.current = id;
      if (pathname.startsWith("/category") && id) {
        showCategorySkeleton();
      }
      return;
    }

    if (previousCategoryIdRef.current !== id) {
      previousCategoryIdRef.current = id;
      if (skipCategorySkeletonRef.current) {
        skipCategorySkeletonRef.current = false;
        return;
      }
      showCategorySkeleton();
    }
  }, [id, dispatch, pathname]);

  const sortItems = async () => {
    const { current: dragInfo } = dragItem;
    const { current: overInfo } = dragOverItem;
    const { current: beforeInfo } = beforeItem;

    if (!dragInfo || !overInfo) return;

    let positionData = { kind: "append" };

    if (beforeInfo.prod && overInfo.prod) {
      positionData = {
        kind: "betweenById",
        after: overInfo.prod.productId,
        before: beforeInfo.prod.productId,
      };
    } else if (beforeInfo.prod) {
      positionData = { kind: "prepend" };
    }

    const payload = {
      branch: stationId,
      position: positionData,
    };

    showAlertWithConfirmCancel({
      msgTitle: "האם לעדכן את סדר הפריטים?",
      confirmText: "אישור",
      cancelText: "ביטול",
      onConfirm: async () => {
        try {
          await changeProdRank(dragInfo.cat, dragInfo.prod.productId, payload);
          const newItemsState = JSON.parse(JSON.stringify(itemsShow));
          updateLocalStateRecursive(newItemsState, dragInfo.cat, dragInfo.prod);
          setItemsShow(newItemsState);
        } catch (error) {
          console.error("Update failed:", error);
        }
      },
    });
  };

  const updateLocalStateRecursive = (node, targetCat, draggedProd) => {
    if (node.slug === targetCat) {
      node.products = node.products.filter(
        (p) => p.productId !== draggedProd.productId,
      );
      const newIndex = dragOverItem.current.pos;
      node.products.splice(newIndex, 0, draggedProd);
      return true;
    }

    if (node.children) {
      for (let child of node.children) {
        if (updateLocalStateRecursive(child, targetCat, draggedProd))
          return true;
      }
    }
    return false;
  };

  const startRenamingCard = (card) => {
    setRenamingCardId(card.id);
    setRenameInputValue(getCategoryName(card));
  };

  const saveRenamingCategory = (cat) => {
    dispatch(
      CategoryUpdate(
        {
          Mkt: cat.id,
          Title: "name",
          value: renameInputValue,
          slug: cat.slug,
        },
        stationId,
      ),
    )
      .then(() => {
        setUpdatedCategories([
          ...updatedCategories.filter((c) => c.id !== cat.id),
          { id: cat.id, name: renameInputValue },
        ]);
      })
      .catch((error) => {
        console.error("Error renaming category:", error);
      });
    setRenamingCardId(null);
  };

  const getCategoryName = (node) => {
    const isUpadated = updatedCategories.find((cat) => cat.id === node.id);
    return isUpadated ? isUpadated.name : node.name;
  };

  const maxItemsToRender = countPage * (pagePlace + 1);
  let renderedItemsCount = 0;

  // Prefer local itemsShow (drag-and-drop edits); fall back to Redux items when
  // local state hasn't synced yet after a fetch — avoids a one-frame "empty" flash.
  const displayRoot = useMemo(() => {
    if (pathname === "/search") return items;
    const source = hasProducts(itemsShow) ? itemsShow : items;
    if (pathname === "/sales") {
      return groupSalesProductsByDepartment(source, categories);
    }
    return source;
  }, [pathname, items, itemsShow, categories]);

  const hasProductsToShow = useMemo(
    () => (displayRoot ? hasProducts(displayRoot) : false),
    [displayRoot],
  );

  const canReorderGroups = Boolean(role) && pathname.startsWith("/category");

  const persistGroupReorder = useCallback(
    async (parentSlug, fromIndex, toIndex, dropMeta = {}) => {
      if (fromIndex === toIndex) return;
      if (groupSavePendingRef.current) return;

      const droppableId = makeGroupDroppableId(parentSlug);
      const listMeta = groupListsRef.current[droppableId];
      const visibleSlugs = listMeta?.familySlugs ?? [];
      const dropError = getGroupDropError({
        sourceDroppableId: dropMeta.sourceDroppableId,
        destinationDroppableId: dropMeta.destinationDroppableId,
        fromIndex,
        toIndex,
        visibleCount: visibleSlugs?.length ?? 0,
      });
      if (dropError) {
        showAlert({
          msgTitle: dropError,
          msgType: "error",
        });
        return;
      }

      const treeRoot = hasProducts(itemsShow) ? itemsShow : items;
      const parent = findNodeBySlug(treeRoot, parentSlug);
      const visibleNodes = visibleSlugs
        .map((slug) => parent?.children?.find((child) => child.slug === slug))
        .filter(Boolean);

      const { nextChildren, moved, reorderedVisible } = permuteVisibleChildren(
        parent?.children,
        visibleNodes,
        fromIndex,
        toIndex,
      );
      const positionPayload = moved
        ? buildGroupPositionPayload(reorderedVisible, toIndex)
        : null;

      if (
        !parent?.children ||
        visibleNodes.length !== visibleSlugs.length ||
        !moved ||
        !isCompleteGroupPositionPayload(positionPayload)
      ) {
        showAlert({
          msgTitle: GROUP_REORDER_ERROR.INVALID_SLOT,
          msgType: "error",
        });
        return;
      }

      const originalRoot = treeRoot;
      const nextTree = replaceChildrenBySlug(
        treeRoot,
        parentSlug,
        nextChildren,
      );

      groupSavePendingRef.current = true;
      setGroupSavePending(true);
      setItemsShow(nextTree);

      const revertOrder = () => {
        setItemsShow(originalRoot);
        groupSavePendingRef.current = false;
        setGroupSavePending(false);
      };

      showAlertWithConfirmCancel({
        msgTitle: "האם לעדכן את סדר המקבצים?",
        confirmText: "אישור",
        cancelText: "בטל",
        onConfirm: async () => {
          try {
            const res = await dispatch(
              CategoryUpdate(
                {
                  slug: moved.slug,
                  Title: "position",
                  value: positionPayload,
                  Mkt: moved.id ?? moved.Id,
                  rank: moved.rank,
                },
                stationId,
              ),
            );
            if (res?.isAxiosError || res instanceof Error) {
              throw res;
            }
            const syncedRoot = replaceChildrenBySlug(
              categoryItemsSource ?? originalRoot,
              parentSlug,
              nextChildren,
            );
            dispatch(setCategoryItemsSource(syncedRoot));
            setSavedGroupSlug(moved.slug);
          } catch (error) {
            console.error("Failed to update group position:", error);
            setItemsShow(originalRoot);
            showAlert({
              msgTitle: getCategoryUpdateErrorMessage(error),
              msgType: "error",
            });
          } finally {
            groupSavePendingRef.current = false;
            setGroupSavePending(false);
          }
        },
        onCancel: revertOrder,
      });
    },
    [categoryItemsSource, dispatch, items, itemsShow, stationId],
  );

  const onGroupDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;
      if (!destination) return;
      if (source.index === destination.index) return;
      if (source.droppableId !== destination.droppableId) {
        showAlert({
          msgTitle: GROUP_REORDER_ERROR.WRONG_PARENT,
          msgType: "error",
        });
        return;
      }
      const parentSlug = parseGroupDroppableId(source.droppableId);
      if (!parentSlug) {
        showAlert({
          msgTitle: GROUP_REORDER_ERROR.INVALID_SLOT,
          msgType: "error",
        });
        return;
      }

      const listMeta = groupListsRef.current[source.droppableId];
      const childNodes = listMeta?.childNodes;
      const resolved = childNodes?.length
        ? resolveFamilyReorderFromListIndices(
            childNodes,
            source.index,
            destination.index,
          )
        : null;

      if (!resolved) {
        showAlert({
          msgTitle: GROUP_REORDER_ERROR.INVALID_SLOT,
          msgType: "error",
        });
        return;
      }

      persistGroupReorder(
        parentSlug,
        resolved.fromFamilyIndex,
        resolved.toFamilyIndex,
        {
          sourceDroppableId: source.droppableId,
          destinationDroppableId: destination.droppableId,
        },
      );
    },
    [persistGroupReorder],
  );

  const renderCategoryNode = (node, sortableMeta = null, level = 0) => {
    if (!node) return null;
    if (renderedItemsCount >= maxItemsToRender) return null;

    let categoryHasRenderedProducts = false;
    const renderedProducts = [];

    if (node.products && node.products.length) {
      let sortedProducts = node.products;
      if (sortKey === "price_asc") {
        sortedProducts = [...node.products].sort(
          (a, b) =>
            parseFloat(a.pricing?.price ?? 0) -
            parseFloat(b.pricing?.price ?? 0),
        );
      } else if (sortKey === "price_desc") {
        sortedProducts = [...node.products].sort(
          (a, b) =>
            parseFloat(b.pricing?.price ?? 0) -
            parseFloat(a.pricing?.price ?? 0),
        );
      }
      for (let i = 0; i < sortedProducts.length; i++) {
        if (renderedItemsCount >= maxItemsToRender) break;

        const element = sortedProducts[i];
        renderedItemsCount++;
        categoryHasRenderedProducts = true;

        renderedProducts.push(
          <Fragment key={element.productId}>
            {element.type === "advertising" ? (
              <div>
                <div className="img" onClick={() => navigate(element.Url)}>
                  <img
                    loading="lazy"
                    src={`${globalFileServer}Advertising/${element.PathUrl}`}
                    alt={element.CategoryName}
                  />
                </div>
                {element.type}
              </div>
            ) : (
              <div
                className="col-lg-2"
                data-product-id={element.productId}
                data-category-id={node.slug}
                data-parent-category-id={node.parentSlug}
                data-family={node.isFamily}
                onDragStart={() => {
                  dragItem.current = { prod: element, cat: node.slug, pos: i };
                }}
                onDragEnter={() => {
                  if (
                    !dragItem.current ||
                    typeof dragItem.current.pos !== "number"
                  ) {
                    return;
                  }
                  dragOverItem.current = {
                    prod:
                      dragItem.current.pos < i ? element : node.products[i - 1],
                    cat: node.slug,
                    pos: i,
                  };
                  beforeItem.current = {
                    prod:
                      dragItem.current.pos < i ? node.products[i + 1] : element,
                    cat: node.slug,
                  };
                }}
                onDragEnd={role ? sortItems : null}
                draggable
              >
                <Card
                  element={element}
                  familyProd={node.isFamily}
                  productPopUp={() => {
                    setProductPopUp(true);
                    setSelectedProd(element);
                    dispatch(setToggleSidebar(false));
                  }}
                  addToShopListPopUp={() => {
                    setAddToShopListPopUp(true);
                    setSelectedProd(element);
                    setPendingListHeartProductId(element.productId);
                  }}
                  listHeartActive={
                    !!listHeartSavedByProductId[String(element.productId)] ||
                    pendingListHeartProductId === element.productId
                  }
                  productLocationPopUp={
                    role
                      ? () => {
                          openProductLocationPopUp(element);
                        }
                      : undefined
                  }
                  comparePricesPopUp={
                    role
                      ? () => {
                          openComparePricePopUp(element);
                        }
                      : undefined
                  }
                  editImagesPopUp={
                    role
                      ? () => {
                          openEditImagesPopUp(element);
                        }
                      : undefined
                  }
                  editSettingsPopUp={
                    role
                      ? () => {
                          openEditSettingsPopUp(element);
                        }
                      : undefined
                  }
                  onAdminStatusClick={
                    role
                      ? (product, nextStatus) =>
                          handleAdminStatusClick(product, node.slug, nextStatus)
                      : undefined
                  }
                  statusPending={
                    !!statusPendingByProductId[String(element.productId)]
                  }
                  alwaysShowBrand={alwaysShowBrand}
                />
              </div>
            )}
          </Fragment>,
        );
      }
    }

    const childNodesToRender = [];
    if (node.children?.length) {
      for (let i = 0; i < node.children.length; i++) {
        if (hasProducts(node.children[i])) {
          childNodesToRender.push(node.children[i]);
        }
      }
    }

    if (
      !categoryHasRenderedProducts &&
      childNodesToRender.length === 0 &&
      !sortableMeta
    ) {
      return null;
    }

    const isPageRoot = id === node.slug;
    const showNonFamilyHeader =
      !isPageRoot &&
      !node.isFamily &&
      node.showHeader !== false &&
      (renderedProducts.length > 0 ||
        (Boolean(role) && childNodesToRender.length > 0));
    const showFamilyHeader = node.isFamily && node.showHeader !== false;
    const showAdminHeaderAccents = Boolean(role) && !isClientView;
    const showRootHeader =
      isPageRoot &&
      Boolean(node.slug) &&
      pathname.startsWith("/category") &&
      showAdminHeaderAccents;
    const showHeader =
      showNonFamilyHeader || showFamilyHeader || showRootHeader;
    const scrollAnchorAttrs = !isPageRoot
      ? {
          "data-category-scroll-anchor": node.slug,
          "data-category-id": node.slug,
          "data-parent-category-id": node.parentSlug,
          "data-family": node.isFamily,
        }
      : null;

    const headerScrollAttrs = scrollAnchorAttrs;

    const scrollAnchor =
      !isPageRoot && !showHeader ? (
        <div
          className="category-scroll-anchor"
          {...scrollAnchorAttrs}
          aria-hidden
        />
      ) : null;

    const headerCategoryLevel =
      level > 0 ? level : getCategoryLevel(node.slug, categories) || 1;
    const titleEditLabel = node.isFamily
      ? "עריכת שם המקבץ"
      : "עריכת שם הקטגוריה";
    const headerProductCount =
      typeof node.totalProducts === "number"
        ? node.totalProducts
        : countTreeProducts(node);
    const productCountBadge =
      role && showHeader ? (
        <span
          className="category-section-header__count"
          aria-label={`${headerProductCount} מוצרים`}
          title={`${headerProductCount} מוצרים`}
        >
          {headerProductCount}
        </span>
      ) : null;
    const sectionTitle =
      renamingCardId === node.id ? (
        <form
          className="rename-form"
          onSubmit={(e) => {
            e.preventDefault();
            saveRenamingCategory(node);
          }}
        >
          <input
            type="text"
            value={renameInputValue}
            onChange={(e) => setRenameInputValue(e.target.value)}
            className="rename-input"
            autoFocus
            aria-label={node.isFamily ? "שם המקבץ" : "שם הקטגוריה"}
          />
          <button
            type="submit"
            className="icon-button"
            aria-label="Save new name"
          >
            <FiSave size={20} />
          </button>
          {productCountBadge}
          {node.isFamily ? (
            <span className="family-price">
              ₪
              {
                renderedProducts[0].props?.children?.props?.children?.props
                  ?.element?.pricing?.price
              }
            </span>
          ) : null}
        </form>
      ) : (
        <h1>
          <span className="category-group__title-text">
            {getCategoryName(node)}
          </span>
          {productCountBadge}
          {role ? (
            <>
              <CategoryAddProductButton
                categorySlug={node.slug}
                categoryId={node.id}
                categoryName={getCategoryName(node)}
                categoryLevel={headerCategoryLevel}
                isFamily={Boolean(node.isFamily)}
                onSuccess={handlePromotionsChanged}
              />
              <button
                type="button"
                className="icon-button category-group__edit-btn"
                onClick={() => startRenamingCard(node)}
                aria-label={titleEditLabel}
              >
                <FiEdit2 size={20} />
              </button>
            </>
          ) : null}
        </h1>
      );

    const categoryLevel = headerCategoryLevel;
    const headerLevelClass = showRootHeader
      ? " category-section-header--root category-section-header--accent"
      : getSectionHeaderLevelClass(
          categoryLevel,
          showFamilyHeader,
          showAdminHeaderAccents,
        );

    const sectionHeader = showHeader ? (
      <div
        className={`category-section-header${headerLevelClass}`}
        {...(headerScrollAttrs || {})}
      >
        {sectionTitle}
      </div>
    ) : null;

    const nestedList =
      childNodesToRender.length > 0 ? (
        <SortableCategoryGroupList
          parentSlug={node.slug}
          childNodes={childNodesToRender}
          renderChild={(child, childSortableMeta) =>
            renderCategoryNode(child, childSortableMeta, categoryLevel + 1)
          }
          savedSlug={savedGroupSlug}
          canReorder={
            canReorderGroups &&
            childNodesToRender.some((child) => child.isFamily)
          }
          isSavePending={groupSavePending}
          onMove={persistGroupReorder}
          listsRef={groupListsRef}
        />
      ) : null;

    const groupBody = (
      <>
        {renderedProducts}
        {nestedList}
      </>
    );

    if (isPageRoot) {
      return (
        <Fragment key={node.id}>
          {scrollAnchor}
          {sectionHeader}
          {groupBody}
        </Fragment>
      );
    }

    if (sortableMeta) {
      return (
        <SortableCategoryGroup
          node={node}
          listIndex={sortableMeta.listIndex}
          familyIndex={sortableMeta.familyIndex}
          listLength={sortableMeta.listLength}
          isSavePending={sortableMeta.isSavePending}
          saved={sortableMeta.saved}
          onMoveUp={sortableMeta.onMoveUp}
          onMoveDown={sortableMeta.onMoveDown}
          heading={sectionHeader}
        >
          {groupBody}
        </SortableCategoryGroup>
      );
    }

    return (
      <div
        key={node.id}
        className="category-group"
        data-category-id={node.slug}
        data-parent-category-id={node.parentSlug}
        data-family={node.isFamily}
      >
        {scrollAnchor}
        {sectionHeader}
        {groupBody}
      </div>
    );
  };

  if (
    currentCategory?.children.length ||
    pathname === "/search" ||
    pathname === "/sales" ||
    pathname.startsWith("/category")
  ) {
    return (
      <div className="products-view">
        <ReactTooltip id="registerTipLocation" place="top" effect="solid">
          לחץ כדי לראות מיקום ולהעביר מלאי.
        </ReactTooltip>
        <ReactTooltip id="registerTipDefinition" place="top" effect="solid">
          לחץ כדי לפתוח הגדרות מוצר.
        </ReactTooltip>
        <ReactTooltip id="registerTipComparePrice" place="top" effect="solid">
          לחץ כדי לעדכן מחיר.
        </ReactTooltip>
        <ReactTooltip id="registerTipEditImages" place="top" effect="solid">
          לחץ כדי לעדכן תמונות.
        </ReactTooltip>
        <ReactTooltip
          id="registerTipAddToList"
          place="top"
          effect="solid"
          delayShow={0}
        >
          הוספה לרשימה
        </ReactTooltip>
        {reloed && pathname.startsWith("/category") && id ? (
          <MobileCategoryPillsSkeleton />
        ) : (
          <MobileCategoryPills
            selectedSlug={mobileSelectedSlug}
            onSelect={setMobileSelectedSlug}
            setPendingScrollSlug={setPendingScrollSlug}
          />
        )}
        <MobileCategoryBreadcrumb
          selectedSlug={mobileSelectedSlug}
          onNavigate={() => setMobileSelectedSlug(null)}
        />

        {productPopUp ? (
          <ProductPageWithImg
            selectedProd={selectedProd}
            close={() => setProductPopUp(!productPopUp)}
            alwaysShowBrand={alwaysShowBrand}
          />
        ) : null}
        {addToShopListPopUp ? (
          <PopUp
            width="500px"
            close={closeAddToShopListPopUp}
            wrapperClassName="popup-wrapper--shopping-list"
          >
            <ProductAddToShoppingList
              selectedProd={selectedProd}
              close={closeAddToShopListPopUp}
              onAddSuccess={(productId) =>
                setListHeartSavedByProductId((m) => ({
                  ...m,
                  [String(productId)]: true,
                }))
              }
            />
          </PopUp>
        ) : null}
        {productLocationPopUp ? (
          <PopUp
            close={closeProductLocationPopUp}
            wrapperClassName="popup-wrapper--product-location"
          >
            <ProductLocationPopUp
              product={selectedProd}
              isModal
              onInventoryUpdated={handleInventoryUpdated}
              onCatalogAvailabilityUpdated={handleCatalogAvailabilityUpdated}
            />
          </PopUp>
        ) : null}
        {comparePricePopUp ? (
          <PopUp
            close={closeComparePricePopUp}
            wrapperClassName="popup-wrapper--compare-price"
          >
            <ComparePricePopUp
              product={selectedProd}
              onPriceSaved={handlePriceSaved}
              onPromotionsChanged={handlePromotionsChanged}
              isModal
            />
          </PopUp>
        ) : null}
        {editImagesPopUp ? (
          <PopUp
            close={closeEditImagesPopUp}
            wrapperClassName="popup-wrapper--product-images"
          >
            <ProductEditImg
              product={selectedProd}
              onMediaSaved={handleMediaSaved}
              isModal
            />
          </PopUp>
        ) : null}
        {editSettingsPopUp ? (
          <PopUp
            close={closeEditSettingsPopUp}
            wrapperClassName="popup-wrapper--product-settings"
          >
            <ProductEdit
              productId={selectedProd?.productId}
              setPendingScrollSlug={setPendingScrollSlug}
              isModal
              onClose={closeEditSettingsPopUp}
              onProductSaved={handleProductFieldsSaved}
            />
          </PopUp>
        ) : null}
        {pathname === "/search" ? (
          <h1 className="search-title">חיפוש מוצרים</h1>
        ) : null}
        {pathname === "/sales" ? (
          <h1 className="search-title">מוצרי מבצע</h1>
        ) : null}

        <div className="flex-container">
          <div className="col-lg-9 category-card category-product-grid">
            {reloed ? (
              pathname === "/search" ? (
                <div className="search-loading" role="status">
                  <span className="search-loading__spinner" aria-hidden />
                  <span>מחפש מוצרים</span>
                </div>
              ) : (
                <ProductsLoadingGrid
                  withHeader={pathname.startsWith("/category")}
                />
              )
            ) : hasProductsToShow || (displayRoot?.totalProducts ?? 0) > 0 ? (
              canReorderGroups ? (
                <DragDropContext onDragEnd={onGroupDragEnd}>
                  {renderCategoryNode(
                    displayRoot,
                    null,
                    getCategoryLevel(displayRoot?.slug, categories) || 1,
                  )}
                </DragDropContext>
              ) : (
                renderCategoryNode(
                  displayRoot,
                  null,
                  getCategoryLevel(displayRoot?.slug, categories) || 1,
                )
              )
            ) : pathname === "/search" ? (
              <h1 className="search-no-results" role="status">
                {searchString?.trim()
                  ? isGtin(searchString)
                    ? `לא נמצא מוצר עם ברקוד (${searchString.trim()})`
                    : `לא נמצאו תוצאות עבור "${searchString.trim()}"`
                  : "לא נמצאו תוצאות לחיפוש"}
              </h1>
            ) : pathname === "/sales" ? (
              <h1>אין מוצרים במבצע</h1>
            ) : (
              <h1>אין מוצרים במלאי</h1>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export default CategoryPage;
